'use client';

import { Suspense, lazy, useEffect, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { track, useFeatureFlags } from '@/lib/telemetry';
import { PROBLEM_REPORT_FLAG, subscribeOpenProblemReport } from '@/lib/problemReport';
import { MAX_TITLE_LENGTH, MIN_MESSAGE_LENGTH, useProblemReportForm } from './useProblemReportForm';
import { useProblemReportSubmit } from './useProblemReportSubmit';
import { EditorErrorBoundary } from './EditorErrorBoundary';
import { TopicCombobox } from './TopicCombobox';

const ProblemReportEditor = lazy(() => import('./ProblemReportEditor'));

const TITLE_ID = 'problem-report-title';
const MESSAGE_LABEL_ID = 'problem-report-message-label';
const MESSAGE_HELP_ID = 'problem-report-message-help';
const MESSAGE_REQUIREMENT = `Descreva o problema com pelo menos ${MIN_MESSAGE_LENGTH} caracteres.`;

function messageCounter(length: number): string {
  if (length < MIN_MESSAGE_LENGTH) return `${length}/${MIN_MESSAGE_LENGTH} caracteres`;
  return `${length} caracteres`;
}

export function ProblemReportDialog() {
  const [open, setOpen] = useState(false);
  // The selected topic's ready-to-use Linear label. Kept outside the form reducer — an optional
  // field that never gates submit (not part of canSubmit).
  const [topicLabel, setTopicLabel] = useState<string | undefined>(undefined);
  const enabled = useFeatureFlags(PROBLEM_REPORT_FLAG);
  const { state, canSubmit, setTitle, setMessage, submitStart, submitSuccess, submitFailure, reset } =
    useProblemReportForm();
  const submit = useProblemReportSubmit();

  useEffect(() => {
    if (!enabled) return;
    return subscribeOpenProblemReport((source) => {
      track('action.problem_report.open', { source });
      setOpen(true);
    });
  }, [enabled]);

  const handleOpenChange = (next: boolean) => {
    if (next) return;
    if (state.status === 'submitting') return;
    setOpen(false);
    reset();
    setTopicLabel(undefined);
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => setTitle(event.target.value);

  const handleFallbackChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setMessage(event.target.value, event.target.value.trim().length);

  const handleCancel = () => handleOpenChange(false);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    submitStart();
    const outcome = await submit({ title: state.title.trim(), body: state.body, topicLabel });
    if (outcome.ok) {
      track('action.problem_report.submit');
      toast.success('Recebemos seu relato. Obrigado!');
      submitSuccess();
      setTopicLabel(undefined);
      setOpen(false);
      return;
    }
    track('problem_report.submit_failed');
    toast.error(outcome.message);
    submitFailure(outcome.message);
  };

  if (!enabled) return null;

  const submitting = state.status === 'submitting';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg motion-reduce:animate-none motion-reduce:transition-none">
        <DialogHeader>
          <DialogTitle>Relatar um problema</DialogTitle>
          <DialogDescription>Conte o que aconteceu. Quanto mais detalhes, melhor.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-4 sm:p-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={TITLE_ID}>Título</Label>
            <Input
              id={TITLE_ID}
              value={state.title}
              onChange={handleTitleChange}
              maxLength={MAX_TITLE_LENGTH}
              disabled={submitting}
              aria-required="true"
              placeholder="Resumo do problema"
            />
          </div>

          <TopicCombobox value={topicLabel} onSelect={setTopicLabel} disabled={submitting} />

          <div className="flex flex-col gap-1.5">
            <Label id={MESSAGE_LABEL_ID}>Mensagem</Label>
            <EditorErrorBoundary
              key={String(open)}
              fallback={
                <Textarea
                  aria-label="Mensagem"
                  aria-describedby={MESSAGE_HELP_ID}
                  disabled={submitting}
                  onChange={handleFallbackChange}
                  className="min-h-32"
                  placeholder="Descreva o problema"
                />
              }
            >
              <Suspense
                fallback={
                  <div className="flex min-h-32 items-center justify-center rounded-xl border border-border-subtle" aria-busy="true">
                    <Spinner />
                  </div>
                }
              >
                <ProblemReportEditor
                  onChange={setMessage}
                  disabled={submitting}
                  labelId={MESSAGE_LABEL_ID}
                  describedById={MESSAGE_HELP_ID}
                />
              </Suspense>
            </EditorErrorBoundary>
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <p id={MESSAGE_HELP_ID}>{MESSAGE_REQUIREMENT}</p>
              <span>{messageCounter(state.messageLength)}</span>
            </div>
            <span className="sr-only" role="status">
              {state.messageLength >= MIN_MESSAGE_LENGTH ? 'Mensagem com o mínimo de caracteres atingido.' : ''}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
